// 用户模块状态管理

import { createWithEqualityFn } from "zustand/traditional";
import { subscribeWithSelector } from "zustand/middleware";
import { shallow } from "zustand/shallow";
import { UserState, UserActions, User, UnpaidOrderItem } from "../types";
import { LoginResponse, RegisterResponse } from "../types/auth.types";
import { userService } from "../services";
import { BaseCache } from "../../base/stores";
// 直接导入，避免走 src/index.ts 反向拉起整套 hooks barrel，造成循环依赖
import utils from "../../../utils";
import { getRechargePage } from "../../../api/records";
import { useStartupStore } from "../../startup/stores";
import { StartupData } from "../../startup/types";
import { SESSION_PENDING_STAGED_FIRST_DEPOSIT_CLAIM } from "../../../constants/firstRechargeStagedSession";

// 创建用户模块专用的缓存实例
const userCache = new BaseCache({
  defaultTTL: 3 * 60 * 1000, // 3分钟
  maxSize: 30,
  enableLogging: true,
});

// 初始状态 - 从本地存储恢复用户信息
const getInitialUserInfo = (): User | null => {
  try {
    const cachedUserInfo = utils.getUser();
    return cachedUserInfo ? JSON.parse(cachedUserInfo) : null;
  } catch (error) {
    console.warn("parse local storage user info failed:", error);
    return null;
  }
};

const initialState: UserState = {
  userInfo: getInitialUserInfo(),
  userLoading: false,
  userError: null,
  loginLoading: false,
  registerLoading: false,
  loginError: null,
  registerError: null,
  unpaidOrders: [],
  // 默认 true，避免首屏在数据到达前闪现 EmptyState
  unpaidOrdersLoading: true,
  cache: new Map(),
};

// 等待启动数据就绪：直接刷新页面时 startupData 可能尚未到达，
// 不能把"未就绪"误判为"功能关闭"，需等其加载完成再决定
const waitForStartupReady = (): Promise<StartupData | null> => {
  const { startupData, startupLoading } = useStartupStore.getState();
  if (
    startupData ||
    startupLoading === "success" ||
    startupLoading === "error"
  ) {
    return Promise.resolve(startupData);
  }
  return new Promise(resolve => {
    const unsubscribe = useStartupStore.subscribe(
      s => s.startupLoading,
      loading => {
        if (loading === "success" || loading === "error") {
          unsubscribe();
          resolve(useStartupStore.getState().startupData);
        }
      }
    );
  });
};

// 创建用户store
export const useUserStore = createWithEqualityFn<UserState & UserActions>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    setUserInfo: data => {
      if (data) {
        set({ userInfo: data });
      } else {
        const cachedUserInfo = utils.getUser();
        set({
          userInfo: cachedUserInfo ? JSON.parse(cachedUserInfo) : null,
        });
      }
    },
    setUserLoading: loading => set({ userLoading: loading }),
    setUserError: error => set({ userError: error }),
    setLoginLoading: loading => set({ loginLoading: loading }),
    setLoginError: error => set({ loginError: error }),
    setRegisterLoading: loading => set({ registerLoading: loading }),
    setRegisterError: error => set({ registerError: error }),
    loadUserInfo: async () => {
      const { setUserLoading, setUserError, setUserInfo } = get();
      setUserLoading(true);
      setUserError(null);
      try {
        const user = await userService.loadUserInfo();
        setUserInfo(user);
        utils.setUser(user);
        setUserLoading(false);

        await get().fetchUnpaidOrders();

        return user;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "load user info failed";
        setUserError(errorMessage);
        setUserLoading(false);
        throw error;
      }
    },

    loadGuestInfo: async () => {
      const { setUserLoading, setUserError, setUserInfo } = get();
      setUserLoading(true);
      setUserError(null);
      try {
        const user = await userService.loadGuestInfo();
        setUserInfo(user);
        utils.setUser(user);
        setUserLoading(false);
        await get().fetchUnpaidOrders();
        return user;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "load guest info failed";
        setUserError(errorMessage);
        setUserLoading(false);
        throw error;
      }
    },
    updateUserBalance: balance =>
      set(state => ({
        userInfo: state.userInfo ? { ...state.userInfo, balance } : null,
      })),
    refreshUserInfo: () => set({ userInfo: get().userInfo }),
    isAuthenticated: () => get().userInfo !== null && !!get().userInfo?.userId,
    setUnpaidOrders: orders => set({ unpaidOrders: orders }),

    logout: () => {
      try {
        sessionStorage.removeItem(SESSION_PENDING_STAGED_FIRST_DEPOSIT_CLAIM);
      } catch {
        /* ignore */
      }
      userService.logout();
    },

    fetchUnpaidOrders: async () => {
      set({ unpaidOrdersLoading: true });
      const startupData = await waitForStartupReady();
      if (!startupData?.paymentVoucherTemplate?.enabled) {
        set({ unpaidOrders: [], unpaidOrdersLoading: false });
        return;
      }
      try {
        const resp = await getRechargePage({
          query: { pageNo: 1, pageSize: 10, status: "PENDING" },
        });
        const page = resp?.page ?? resp?.data?.page;
        const content: any[] = page?.content ?? [];
        const items: UnpaidOrderItem[] = content
          .filter((r: any) => !r.subCurrencyFlag)
          .map((r: any) => {
            const offline = r.offlineTokenWalletData;
            const voucherImgs =
              Array.isArray(r.paymentVoucherImgs) &&
              r.paymentVoucherImgs.length > 0
                ? r.paymentVoucherImgs
                : Array.isArray(offline?.certificateImgs) &&
                    offline.certificateImgs.length > 0
                  ? offline.certificateImgs
                  : undefined;
            const numIds =
              Array.isArray(r.numberIds) && r.numberIds.length > 0
                ? r.numberIds
                : Array.isArray(offline?.numberIds) &&
                    offline.numberIds.length > 0
                  ? offline.numberIds
                  : undefined;
            return {
              id: r.id,
              orderNo: r.orderNo,
              amount: r.amount,
              // 订单币种；后端返回的 RechargeRecord 有 currencyId、没有 currencySymbol，
              // 符号由展示层按它查 startup 的货币表（见 OrderCard）。
              currencyId: r.currencyId,
              currencySymbol: r.currencySymbol,
              status: r.status,
              created: r.created,
              paymentVoucherImgs: voucherImgs,
              numberIds: numIds,
              rejectTemplateContent: r.rejectTemplateContent,
              subCurrencyFlag: r.subCurrencyFlag,
            };
          });
        set({ unpaidOrders: items, unpaidOrdersLoading: false });
      } catch (error) {
        console.warn("fetchUnpaidOrders failed:", error);
        set({ unpaidOrders: [], unpaidOrdersLoading: false });
      }
    },

    setCache: (key, data, ttl) => {
      userCache.setCache(key, data, ttl);
      set({ cache: new Map(userCache["cache"]) });
    },

    getCache: key => {
      return userCache.getCache(key);
    },

    clearCache: pattern => {
      userCache.clearCache(pattern);
      set({ cache: new Map(userCache["cache"]) });
    },

    hasCache: key => {
      return userCache.hasCache(key);
    },

    getCacheKeys: () => {
      return userCache.getCacheKeys();
    },

    getCacheSize: () => {
      return userCache.getCacheSize();
    },

    cacheFirst: async (key, apiCall, ttl) => {
      return userCache.cacheFirst(key, apiCall, ttl);
    },
    login: async data => {
      const { setLoginLoading, setLoginError, setUserInfo } = get();
      setLoginLoading(true);
      setLoginError(null);
      try {
        const result: LoginResponse = await userService.login(data);
        setUserInfo(result.user);
        utils.setUser(result.user);
        utils.markEverLoggedIn();
        setLoginLoading(false);
        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "login failed";
        setLoginError(errorMessage);
        setLoginLoading(false);
        throw error;
      }
    },
    register: async data => {
      const { setRegisterLoading, setRegisterError, setUserInfo } = get();
      setRegisterLoading(true);
      setRegisterError(null);
      try {
        const result: RegisterResponse = await userService.register(data);
        setUserInfo(result.user);
        utils.setUser(result.user);
        utils.markEverLoggedIn();
        setRegisterLoading(false);
        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "register failed";
        setRegisterError(errorMessage);
        setRegisterLoading(false);
        throw error;
      }
    },
  }))
);

// 选择器hooks
export const useUserData = () =>
  useUserStore(state => ({
    data: state.userInfo,
    loading: state.userLoading,
    error: state.userError,
  }));

export const useUserAuth = () =>
  useUserStore(state => ({
    isAuthenticated: state.isAuthenticated(),
    loginLoading: state.loginLoading,
    registerLoading: state.registerLoading,
    loadUserInfo: state.loadUserInfo,
    loadGuestInfo: state.loadGuestInfo,
    loginError: state.loginError,
    registerError: state.registerError,
    login: state.login,
    register: state.register,
    logout: state.logout,
  }));

// 扁平化的用户数据 hook - 直接导出 User 的所有属性并整合认证功能
export const useUserDataFlattened = () =>
  useUserStore(state => {
    const {
      userInfo,
      userLoading,
      userError,
      loginLoading,
      registerLoading,
      loginError,
      registerError,
    } = state;

    // 如果 userInfo 为 null，返回所有属性为 undefined
    if (!userInfo) {
      return {
        // 状态信息
        loading: userLoading,
        error: userError,
        data: null,

        // 认证相关状态和方法
        isAuthenticated: false,
        loginLoading,
        registerLoading,
        loginError,
        registerError,

        // 认证相关方法
        loadUserInfo: state.loadUserInfo,
        login: state.login,
        register: state.register,
        logout: state.logout,
        updateUserBalance: state.updateUserBalance,
        refreshUserInfo: state.refreshUserInfo,

        // User 的所有属性，当 userInfo 为 null 时都设为 undefined
        userId: undefined,
        username: undefined,
        root: undefined,
        big: undefined,
        center: undefined,
        showEncryptData: undefined,
        out: undefined,
        token: undefined,
        selB: undefined,
        currRatio: undefined,
        currSymbol: undefined,
        createdTime: undefined,
        auth2Verify: undefined,
        email: undefined,
        phone: undefined,
        phoneChecked: undefined,
        avatar: undefined,
        balance: undefined,
        taskBonusBalance: undefined,
        depositForWithdraw: undefined,
        registerTaskBonusBalance: undefined,
        cumulativeBetAmount: undefined,
        firstRechargeFlag: undefined,
        canPlayThirdPartyGame: undefined,
        firstRechargePopupShowPercent: undefined,
        firstRechargeRulePromoRatio: undefined,
        thirdGameWalletList: undefined,
        vipLevel: undefined,
        nextlevel: undefined,
        thirdLoginTypes: undefined,
        bind2Auth: undefined,
        firstRechargeRuleAmount: undefined,
        customRechargeRules: undefined,
        redpacket: undefined,
        betAmount: undefined,
        rechargeAmount: undefined,
        withdrawAmount: undefined,
        bonusTaskCheckInDay: undefined,
        todayCheckIn: undefined,
        upgraded: undefined,
        noticeUpdateTime: undefined,
        customRechargeRuleAmount: undefined,
        whatsapp: undefined,
        preferences: undefined,
        userCheckIn: undefined,
        redPacketReceivable: undefined,
        rechargeCount: undefined,
        dailyRechargeTasks: undefined,
        dailyRechargeAllCompleted: undefined,
        dailyRechargeTask: undefined,
        dailyBetTask: undefined,
      };
    }

    // 当 userInfo 存在时，展开所有属性
    return {
      // 状态信息
      loading: userLoading,
      error: userError,
      data: userInfo,

      // 认证相关状态和方法
      isAuthenticated: !!userInfo.userId,
      loginLoading,
      registerLoading,
      loginError,
      registerError,

      // 认证相关方法
      loadUserInfo: state.loadUserInfo,
      login: state.login,
      register: state.register,
      logout: state.logout,
      updateUserBalance: state.updateUserBalance,
      refreshUserInfo: state.refreshUserInfo,

      // User 的所有属性
      ...userInfo,
    };
  }, shallow);

export function useUserCache() {
  const { cache } = useUserStore(state => ({
    cache: state.cache,
  }));
  return cache;
}

export const useUnpaidOrders = () =>
  useUserStore(
    state => ({
      unpaidOrders: state.unpaidOrders,
      unpaidOrdersLoading: state.unpaidOrdersLoading,
      hasUnpaidOrders: state.unpaidOrders.length > 0,
      fetchUnpaidOrders: state.fetchUnpaidOrders,
    }),
    shallow
  );
